/**
 * Owner: Masa Bayu
 * File: LandingSections.tsx
 * Date:Time: 2026-10-18 16:35
 * Description: Home landing §376 — zon kanvas utama: strip kategori §408 (dua
 *   baris + More → /categories), rak buku (featured/trending), Continue
 *   Reading, banner Explore. §457 F6 — CTA Create dibuang daripada home;
 *   daftar penerbit hanya melalui /account dan skrin /become-an-author kekal.
 *   §468i — CollectionShelves: rak koleksi (pengarang/kategori) di atas
 *   Featured Books; rak kosong daripada katalog hub tidak dipaparkan.
 */

import type { ReactElement } from 'react';
import { useHubCoverSrc } from '../../../publication/use-hub-cover';
import {
  MARKETPLACE_CATEGORY_ORDER,
  type MarketplaceBook,
  type MarketplaceCategoryId,
} from '../marketplace-projection';
import { formatMarketplacePriceOrUnknown } from '../marketplace-locale';
import type { LandingLabels } from './landing-locale';
import { LandingIcon } from './LandingIcons';
import type { LandingIconName } from './LandingIcons';
import { CATEGORY_ICON, go } from './landing-shared';
import type { LandingCollection, LandingCollectionId, LandingContinueEntry } from './landing-data';

/** §408 — dua baris sahaja (All + 8 + More); More membuka halaman /categories.
 *  §409 — pilihan kategori menapis rak buku home di tempat (tiada navigasi). */
const STRIP_CATEGORY_COUNT = 8;

export function CategoryStrip({
  t,
  tc,
  onNavigate,
  filterCat,
  onFilterCat,
}: {
  readonly t: LandingLabels;
  readonly tc: Readonly<Record<MarketplaceCategoryId, string>>;
  readonly onNavigate: (path: string) => void;
  readonly filterCat: MarketplaceCategoryId | 'all';
  readonly onFilterCat: (next: MarketplaceCategoryId | 'all') => void;
}): ReactElement {
  const visible = MARKETPLACE_CATEGORY_ORDER.slice(0, STRIP_CATEGORY_COUNT);
  return (
    <nav className="qxl__strip" aria-label={t.categoryStrip.ariaLabel}>
      <button
        type="button"
        className={filterCat === 'all' ? 'qxl__strip-item is-active' : 'qxl__strip-item'}
        onClick={() => {
          onFilterCat('all');
        }}
      >
        <span className="qxl__strip-icon" aria-hidden="true">
          <LandingIcon name="book" size={16} />
        </span>
        <span>{t.categoryStrip.all}</span>
      </button>
      {visible.map((id) => (
        <button
          key={id}
          type="button"
          className={filterCat === id ? 'qxl__strip-item is-active' : 'qxl__strip-item'}
          onClick={() => {
            onFilterCat(id);
          }}
        >
          <span className="qxl__strip-icon" aria-hidden="true">
            <LandingIcon name={CATEGORY_ICON[id]} size={16} />
          </span>
          <span>{tc[id]}</span>
        </button>
      ))}
      <button
        type="button"
        className="qxl__strip-item"
        onClick={() => {
          onNavigate('/categories');
        }}
      >
        <span className="qxl__strip-icon" aria-hidden="true">
          <LandingIcon name="more" size={16} />
        </span>
        <span>{t.categoryStrip.more}</span>
      </button>
    </nav>
  );
}

function SectionHead({
  id,
  icon,
  title,
  seeAll,
  onSeeAll,
}: {
  readonly id: string;
  readonly icon: LandingIconName;
  readonly title: string;
  readonly seeAll: string;
  readonly onSeeAll: () => void;
}): ReactElement {
  return (
    <div className="qxl__section-head">
      <h2 id={id} className="qxl__section-title">
        <LandingIcon
          name={icon}
          size={19}
          className={`qxl__section-icon qxl__section-icon--${icon}`}
        />
        {title}
      </h2>
      <button type="button" className="qxl__see-all" onClick={onSeeAll}>
        {seeAll} <LandingIcon name="arrowRight" size={14} />
      </button>
    </div>
  );
}

function BookTile({
  book,
  t,
  currency,
  categoryLabel,
  wished,
  onWish,
  onNavigate,
  variant = 'featured',
}: {
  readonly book: MarketplaceBook;
  readonly t: LandingLabels;
  readonly currency: 'USD' | 'MYR';
  readonly categoryLabel: string | null;
  readonly wished: boolean;
  readonly onWish: (publicationId: string) => void;
  readonly onNavigate: (path: string) => void;
  readonly variant?: 'featured' | 'trending';
}): ReactElement {
  const detailPath = `/publication/${encodeURIComponent(book.publicationId)}`;
  const coverSrc = useHubCoverSrc(book.publicationId);
  return (
    <li className={`qxl__tile qxl__tile--${variant}`}>
      <button
        type="button"
        className="qxl__tile-cover-btn"
        aria-label={book.title}
        onClick={() => {
          onNavigate(detailPath);
        }}
      >
        <span className="qxl__tile-cover" data-tone={book.coverTone}>
          {/* COVER-A3 — cover sebenar dari bundle; kotak nada hanya lalai bila tiada cover. */}
          {coverSrc === null ? (
            <span className="qxl__tile-cover-title">{book.title}</span>
          ) : (
            <img className="qxl__tile-cover-img" src={coverSrc} alt={book.title} />
          )}
          {categoryLabel === null ? null : <span className="qxl__tile-chip">{categoryLabel}</span>}
        </span>
      </button>
      <button
        type="button"
        className={wished ? 'qxl__tile-bookmark is-on' : 'qxl__tile-bookmark'}
        aria-label={wished ? t.card.bookmarkRemove : t.card.bookmarkAdd}
        aria-pressed={wished}
        onClick={() => {
          onWish(book.publicationId);
        }}
      >
        <LandingIcon name="bookmark" size={15} />
      </button>
      <span className="qxl__tile-meta">
        <button
          type="button"
          className="qxl__tile-title"
          onClick={() => {
            onNavigate(detailPath);
          }}
        >
          {book.title}
        </button>
        {book.author === null ? null : <p className="qxl__tile-author">{book.author}</p>}
        <p className="qxl__tile-price">
          {formatMarketplacePriceOrUnknown(book.priceMinorUnits, currency, t.card.priceUnknown)}
        </p>
      </span>
    </li>
  );
}

export function BookShelfSection({
  id,
  icon,
  title,
  seeAll,
  onSeeAll,
  gridClassName,
  gridAriaLabel,
  books,
  t,
  currency,
  categoryLabels,
  wish,
  onWish,
  onNavigate,
  variant = 'featured',
  emptyText,
}: {
  readonly id: string;
  readonly icon: LandingIconName;
  readonly title: string;
  readonly seeAll: string;
  readonly onSeeAll: () => void;
  readonly gridClassName: string;
  readonly gridAriaLabel: string;
  readonly books: readonly MarketplaceBook[];
  readonly t: LandingLabels;
  readonly currency: 'USD' | 'MYR';
  readonly categoryLabels: Readonly<Record<MarketplaceCategoryId, string>>;
  readonly wish: ReadonlySet<string>;
  readonly onWish: (publicationId: string) => void;
  readonly onNavigate: (path: string) => void;
  readonly variant?: 'featured' | 'trending';
  readonly emptyText: string;
}): ReactElement {
  return (
    <section className="qxl__section" aria-labelledby={id}>
      <SectionHead id={id} icon={icon} title={title} onSeeAll={onSeeAll} seeAll={seeAll} />
      {books.length === 0 ? (
        <p className="qxl__shelf-empty">{emptyText}</p>
      ) : (
        <ul className={gridClassName} aria-label={gridAriaLabel}>
          {books.map((book) => (
            <BookTile
              key={book.publicationId}
              book={book}
              t={t}
              currency={currency}
              categoryLabel={book.categoryId === null ? null : categoryLabels[book.categoryId]}
              wished={wish.has(book.publicationId)}
              onWish={onWish}
              onNavigate={onNavigate}
              variant={variant}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function ContinueReading({
  t,
  entries,
  onNavigate,
}: {
  readonly t: LandingLabels;
  readonly entries: readonly LandingContinueEntry[];
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <section className="qxl__section qxl__section--continue" aria-labelledby="qxl-continue">
      <SectionHead
        id="qxl-continue"
        icon="book"
        title={t.sections.continueReading}
        onSeeAll={() => {
          go(onNavigate, '/library');
        }}
        seeAll={t.sections.seeAll}
      />
      <ul className="qxl__continue" aria-label={t.card.continueGridAria}>
        {entries.map((entry) => (
          <li key={entry.publicationId} className="qxl__continue-card">
            <button
              type="button"
              className="qxl__continue-open"
              onClick={() => {
                go(onNavigate, `/read/${encodeURIComponent(entry.publicationId)}`);
              }}
            >
              <span className={`qxl__mini-cover`} aria-hidden="true">
                {entry.title.slice(0, 2)}
              </span>
              <span className="qxl__continue-meta">
                <strong>{entry.title}</strong>
                <span className="qxl__continue-bar" aria-hidden="true">
                  <span
                    className="qxl__continue-fill"
                    style={{ width: `${Math.min(100, Math.max(0, entry.percent))}%` }}
                  />
                </span>
                <small>
                  {entry.chapter !== null
                    ? `${t.card.chapter} ${String(entry.chapter)}`
                    : `${String(entry.percent)}%`}
                  {' · '}
                  {String(entry.percent)}%
                </small>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ExploreBanner({
  t,
  onNavigate,
}: {
  readonly t: LandingLabels;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <div className="qxl__explore">
      <div className="qxl__explore-text">
        <p className="qxl__explore-title">{t.sections.exploreTitle}</p>
        <p className="qxl__explore-sub">{t.sections.exploreSub}</p>
        <div className="qxl__explore-chips">
          <button
            type="button"
            className="qxl__explore-chip"
            onClick={() => go(onNavigate, '/bestsellers')}
          >
            <LandingIcon name="star" size={15} /> {t.sections.bestSellers}
          </button>
          <button
            type="button"
            className="qxl__explore-chip"
            onClick={() => go(onNavigate, '/collections')}
          >
            <LandingIcon name="sparkle" size={15} /> {t.sections.newReleases}
          </button>
          {/* §466 S1 — chip "Top Rated" dibuang: tiada data penarafan di hub. */}
          <button
            type="button"
            className="qxl__explore-chip"
            onClick={() => go(onNavigate, '/collections')}
          >
            <LandingIcon name="grid" size={15} /> {t.sections.curated}
          </button>
        </div>
      </div>
      <button
        type="button"
        className="qxl__pill qxl__pill--primary"
        onClick={() => {
          go(onNavigate, '/all-books');
        }}
      >
        {t.sections.browseAll} <LandingIcon name="arrowRight" size={15} />
      </button>
    </div>
  );
}

/* §468i — rak koleksi di atas Featured Books. Rak tanpa buku daripada katalog hub
   tidak dipaparkan (pola ContinueReading); tiada senarai buku rekaan. */
const COLLECTION_ICON: Record<LandingCollectionId, LandingIconName> = {
  masaBayu: 'user',
  alamtologi: 'bookmark',
};

export function CollectionShelves({
  collections,
  t,
  currency,
  categoryLabels,
  wish,
  onWish,
  onNavigate,
}: {
  readonly collections: readonly LandingCollection[];
  readonly t: LandingLabels;
  readonly currency: 'USD' | 'MYR';
  readonly categoryLabels: Readonly<Record<MarketplaceCategoryId, string>>;
  readonly wish: ReadonlySet<string>;
  readonly onWish: (publicationId: string) => void;
  readonly onNavigate: (path: string) => void;
}): ReactElement {
  return (
    <>
      {collections.map((collection) => (
        <BookShelfSection
          key={collection.id}
          id={`qxl-collection-${collection.id}`}
          icon={COLLECTION_ICON[collection.id]}
          title={t.sections.collections[collection.id]}
          seeAll={t.sections.seeAll}
          onSeeAll={() => {
            go(onNavigate, collection.seeAllPath);
          }}
          gridClassName="qxl__grid qxl__grid--featured"
          gridAriaLabel={t.sections.collections[collection.id]}
          books={collection.books}
          t={t}
          currency={currency}
          categoryLabels={categoryLabels}
          wish={wish}
          onWish={onWish}
          onNavigate={onNavigate}
          emptyText={t.sections.emptyShelf}
        />
      ))}
    </>
  );
}
